/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  getAuditoriaErros,
  listarVinculosBots,
  IAuditoriaErroItem,
  IAuditoriaErrosParams,
  IEmpresaResumo,
  StatusAuditoriaFiltro,
} from '../api';
import { C, FONTE_MONO, FONTE_TEXTO, TONES } from '../paleta';
import { SelectFiltro } from '../components/SelectFiltro';
import { NomeEmpresa } from '../components/NomeEmpresa';
import { ModalPrint } from '../components/ModalPrint';
import { BuscaEmpresa } from '../components/BuscaEmpresa';
import { CARTAO, estiloPilula, fundoDaAba, Icone, TituloSecao } from '../components/Vidro';
import { opcoesDeEmpresas } from '../utils/buscaEmpresa';
import { nomeEmpresa } from '../utils/derivarProcessos';
import { useApelidos } from '../useApelidos';
import {
  avisoJanelaClampada,
  celulaEtapa,
  classificacaoDaLinha,
  DadosHeadlineErros,
  ETAPAS_AUDITORIA,
  formatarTsAuditoria,
  janelaEfetiva,
  mensagemDaLinha,
  paginacaoAuditoria,
  paramsDoPeriodoAuditoria,
  PeriodoAuditoria,
  PresetAuditoria,
  rangeDoPresetAuditoria,
  sufixoRodapeErros,
} from '../utils/auditoriaErros';
import { acharItemDoFoco, lerFiltrosDaQuery } from '../utils/erroDoPopup';
import {
  INTERVALO_AO_VIVO_MS,
  intervaloDeRefetch,
  lerPreferenciaAoVivo,
  salvarPreferenciaAoVivo,
} from '../utils/preferenciaAoVivo';

/**
 * Aba ERROS — auditoria de ocorrências erro/warning da esteira (pedido 09/09:
 * "temos muitos dados, precisamos dessa tela de auditoria"): TABELA de qual bot
 * quebrou, tipo de erro, empresa, etapa (quando o bot envia), mensagem, datetime
 * e o print quando houver, na linguagem visual do protótipo (`.orq-proto`).
 *
 * Fonte: `GET /orquestrador/auditoria/erros` (BFF → mon), paginação SERVER-SIDE
 * (limit 50) e `resumo.por_tipo`/`por_bot` agregados do MESMO filtro alimentando
 * os mini-cards do topo. Query TanStack keyada pelos filtros e staleTime 30s.
 *
 * Polling OPT-IN (pedido 09/09: "coloque um pooling de 5 segundos na tela de erro
 * tb pfv e um toggle botton para habilitar ou desabilitar ele"): o toggle "Ao
 * vivo" liga `refetchInterval` de 5s, e nasce DESLIGADO. A consulta varre trilha
 * time-series volumosa no Mongo (7 dias por padrão) e ainda agrega top-10 por
 * tipo e por bot na mesma passada — auditoria é leitura investigativa, então o
 * custo contínuo só existe quando alguém explicitamente pede para acompanhar. A
 * preferência fica no localStorage (`utils/preferenciaAoVivo`), então voltar para
 * a aba restaura a escolha; sem storage, cai no default desligado sem quebrar.
 * `refetchIntervalInBackground` fica DESLIGADO de propósito: o TanStack pausa o
 * polling em aba de fundo e é isso que se quer — ninguém varre o Mongo enquanto
 * a página está escondida.
 *
 * Decisões de contrato que moldam a UI:
 * - o endpoint NÃO tem filtro por tipo_erro; como o tipo DERIVA da própria
 *   mensagem no mon (classe de exceção achada nela, ou o prefixo da 1ª linha),
 *   o clique num mini-card de tipo aplica a busca textual `q` com o tipo — o
 *   servidor filtra a mesma população que agrupou (aproximação documentada);
 * - `etapa` é catálogo FECHADO (400 fora dele) → select com o espelho das 8
 *   canônicas, nunca texto livre;
 * - o clamp de 31 dias do servidor (`janela_clampada`) vira aviso discreto com
 *   o `de_efetivo` — nunca silencioso;
 * - o print é ADMIN-ONLY (grupos 1/5, LGPD) — o ModalPrint busca os bytes via
 *   fetch autenticado (mesma assimetria do ScreenshotViewer do /monitoramento)
 *   e trata o 403 com mensagem digna.
 */

const LIMITE_PAGINA = 50;
const DEBOUNCE_MS = 400;

const OPCOES_STATUS: Array<{ valor: StatusAuditoriaFiltro; label: string }> = [
  { valor: 'erro', label: 'Erro' },
  { valor: 'warning', label: 'Warning' },
  { valor: 'ambos', label: 'Ambos' },
];

const gridColunas =
  'minmax(0, 0.75fr) minmax(0, 0.95fr) minmax(0, 1.15fr) minmax(0, 0.6fr) minmax(0, 0.75fr) minmax(0, 1fr) minmax(0, 1.9fr) minmax(0, 0.55fr) minmax(0, 0.75fr)';

const COLUNAS = [
  'Data/hora',
  'Bot',
  'Empresa',
  'Competência',
  'Etapa',
  'Tipo de erro',
  'Mensagem',
  'Status',
  '',
];

/**
 * Prefixo do `title` dos chips de resumo: a UNIDADE do número, mais a ressalva de
 * que ocorrência não é empresa (achado 5 da auditoria 09/09).
 */
const unidadeDoChip = (qtd: number): string =>
  `${qtd} ${qtd === 1 ? 'ocorrência' : 'ocorrências'} (uma mesma empresa pode repetir em retentativas)`;

/** Chip dos mini-cards "Top tipos de erro"/"Top bots" — toggle que aplica filtro. */
const ChipResumo: React.FC<{
  label: string;
  qtd: number;
  ativo: boolean;
  mono?: boolean;
  title?: string;
  onClick?: () => void;
}> = ({ label, qtd, ativo, mono = false, title, onClick }) => (
  <div
    onClick={onClick}
    title={title}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      padding: '4px 10px',
      borderRadius: '999px',
      border: `1px solid ${ativo ? C.dark : C.border}`,
      background: ativo ? C.dark : 'oklch(1 0 0)',
      color: ativo ? C.onDark : 'oklch(0.4 0.02 165)',
      fontSize: '11.5px',
      fontWeight: 600,
      cursor: onClick ? 'pointer' : 'default',
      maxWidth: '100%',
      userSelect: 'none',
    }}
  >
    <span
      style={{
        fontFamily: mono ? FONTE_MONO : FONTE_TEXTO,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '260px',
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontFamily: FONTE_MONO,
        fontSize: '10.5px',
        fontWeight: 400,
        color: ativo ? C.onDarkSub : C.faint,
        flex: 'none',
      }}
    >
      {qtd}
    </span>
  </div>
);

/** Bloco rotulado da linha expandida (label mono uppercase + valor). */
const CampoDetalhe: React.FC<{ label: string; children: React.ReactNode; largo?: boolean }> = ({
  label,
  children,
  largo = false,
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      minWidth: 0,
      ...(largo ? { flex: '1 1 100%' } : {}),
    }}
  >
    <div
      style={{
        fontFamily: FONTE_MONO,
        fontSize: '9.5px',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: C.faint,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: '12.5px', color: 'oklch(0.35 0.02 165)', overflowWrap: 'anywhere' }}>
      {children}
    </div>
  </div>
);

interface ErrosTabProps {
  /** Sobe total/top-tipo/top-bot pro index (headline calculada + contador da aba). */
  // eslint-disable-next-line no-unused-vars
  onDadosChange?: (dados: DadosHeadlineErros | null) => void;
  /**
   * Query string da página (09/09) — a aba INICIALIZA os filtros a partir dela
   * quando o clique na mensagem do popup mandou um recorte. Ausente ou sem
   * parâmetros conhecidos = defaults de sempre (7 dias, status "ambos").
   */
  inicial?: URLSearchParams;
  /**
   * Empresas da página (o mesmo dataset do Processos) — só para a busca por
   * RAZÃO SOCIAL do filtro de empresa (pedido 19/10: "igual está na Resumo").
   * Ausente, a busca por nome fica sem opções e o modo ID segue funcionando.
   */
  empresas?: IEmpresaResumo[];
}

/** Modo da busca de empresa — ID primeiro: "é o que mais usamos" (19/10). */
type ModoEmpresa = 'id' | 'razao';

export const ErrosTab: React.FC<ErrosTabProps> = ({ onDadosChange, inicial, empresas }) => {
  // Lido UMA vez na montagem: a partir daí o estado é da aba (mexer nos filtros
  // não reescreve a URL). Trocar de recorte pela URL remonta a aba (`key` no index).
  const [daQuery] = useState(() => lerFiltrosDaQuery(inicial ?? new URLSearchParams()));

  // A ponte do popup NÃO manda período (derivar o dia do `ts` estourava o fuso —
  // ver `utils/erroDoPopup`): a aba abre sempre com a janela padrão dela.
  //
  // 31d desde 21/09: a tela toda passou a abrir em 30 dias, e a aba ficar em 7
  // fazia a falha que o usuário via no Processos não aparecer aqui. 31 (e não 30)
  // porque é o teto do servidor (`_MAX_DIAS_JANELA`) e o preset já encostava nele.
  const [periodo, setPeriodo] = useState<PeriodoAuditoria>(() => rangeDoPresetAuditoria('31d'));
  const [status, setStatus] = useState<StatusAuditoriaFiltro>(daQuery.status ?? 'ambos');
  const [bot, setBot] = useState<string | null>(daQuery.bot);
  const [etapa, setEtapa] = useState<string | null>(null);
  // Busca/empresa digitadas × APLICADAS: o texto entra na query só depois do
  // debounce de 400ms (clique no chip de tipo aplica na hora, sem esperar).
  const [q, setQ] = useState('');
  const [qAplicado, setQAplicado] = useState('');
  const [codiEmp, setCodiEmp] = useState(daQuery.codiEmp);
  // Empresa vinda da URL já entra APLICADA — esperar o debounce faria a primeira
  // busca sair sem o filtro e a linha do foco cairia fora da página.
  const [codiEmpAplicado, setCodiEmpAplicado] = useState(daQuery.codiEmp);
  const [modoEmpresa, setModoEmpresa] = useState<ModoEmpresa>('id');
  const opcoesBuscaEmpresa = useMemo(
    () => opcoesDeEmpresas(empresas ?? [], nomeEmpresa),
    [empresas],
  );
  const [offset, setOffset] = useState(0);
  const [expandida, setExpandida] = useState<string | null>(null);
  const [hoverLinha, setHoverLinha] = useState<string | null>(null);
  const [printKey, setPrintKey] = useState<string | null>(null);
  /** Linha em destaque (flash + scroll) — só a vinda do clique no popup. */
  const [linhaFoco, setLinhaFoco] = useState<string | null>(null);
  const focoAplicado = useRef(false);
  /**
   * Toggle "Ao vivo" (polling de 5s). Inicializador PREGUIÇOSO: o localStorage é
   * lido uma vez na montagem — a cada volta para a aba o componente remonta e a
   * escolha anterior é restaurada. Default desligado (ver `preferenciaAoVivo`).
   */
  const [aoVivo, setAoVivo] = useState(() => lerPreferenciaAoVivo());

  const { apelidoDoBot, temApelido } = useApelidos();

  useEffect(() => {
    const t = setTimeout(() => {
      setQAplicado(q);
      setCodiEmpAplicado(codiEmp);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, codiEmp]);

  // Mudou o texto EFETIVO da busca/empresa → volta pra primeira página.
  useEffect(() => {
    setOffset(0);
  }, [qAplicado, codiEmpAplicado]);

  const params = useMemo((): IAuditoriaErrosParams => {
    const base: IAuditoriaErrosParams = {
      ...paramsDoPeriodoAuditoria(periodo),
      status,
      limit: LIMITE_PAGINA,
      offset,
    };
    if (bot) base.bot = bot;
    if (etapa) base.etapa = etapa;
    if (qAplicado.trim()) base.q = qAplicado.trim();
    if (codiEmpAplicado.trim()) base.codi_emp = codiEmpAplicado.trim();
    return base;
  }, [periodo, status, bot, etapa, qAplicado, codiEmpAplicado, offset]);

  const { data, isLoading, isError, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['orqAuditoriaErros', params],
    queryFn: () => getAuditoriaErros(params),
    // staleTime NÃO interfere no polling: o `refetchInterval` do TanStack v5
    // dispara o fetch do tick incondicionalmente, sem consultar se o dado está
    // fresco. Por isso o cache de 30s de sempre continua valendo igual com o
    // "Ao vivo" ligado ou desligado — não existe versão especial dele aqui.
    staleTime: 30000,
    refetchInterval: intervaloDeRefetch(aoVivo),
    // `refetchIntervalInBackground` NÃO é ligado de propósito: em aba de fundo o
    // TanStack pausa o intervalo — é o comportamento desejado (nada de varrer o
    // Mongo com a página escondida).
    // Erro não vira loop agressivo: com retry 1 e intervalo de 5s, o pior caso é
    // uma tentativa por tick, e o TanStack não reencavala fetch com fetch em voo.
    retry: 1,
    // Troca de página/filtro mantém a tabela anterior visível (com opacidade
    // reduzida) em vez de piscar pro estado de carregamento. É também o que faz
    // o tick do polling ser invisível: a tabela atual segue na tela até a
    // resposta nova chegar, e a aba não desmonta — scroll, modal de print e o
    // estado dos filtros continuam onde estavam.
    // O que NÃO é garantido: linha expandida e destaque de foco são presos ao
    // `id` da ocorrência. Se a esteira viva empurrar esse id para fora da página
    // entre dois ticks, o detalhe fecha e o destaque some — daí o aviso de
    // `saiuDaPagina` abaixo, para o sumiço não ser silencioso.
    placeholderData: keepPreviousData,
  });

  // Persiste a escolha a CADA mudança (não só no clique): storage bloqueado só
  // significa preferência não guardada — `salvarPreferenciaAoVivo` não lança.
  useEffect(() => {
    salvarPreferenciaAoVivo(aoVivo);
  }, [aoVivo]);

  const { data: vinculos } = useQuery({
    queryKey: ['orqVinculosBots'],
    queryFn: () => listarVinculosBots(),
  });

  // Headline/contador do index derivam da MESMA resposta da tabela.
  useEffect(() => {
    if (!onDadosChange) return;
    if (!data) {
      onDadosChange(null);
      return;
    }
    const topTipo = data.resumo.por_tipo[0]?.tipo_erro ?? null;
    const topBot = data.resumo.por_bot[0]?.bot ?? null;
    onDadosChange({
      total: data.total,
      topTipo,
      topBot: topBot ? apelidoDoBot(topBot) : null,
      // A janela é a EFETIVA do servidor (já com o clamp de 31 dias aplicado) —
      // a headline precisa dizer de quando é o número, porque a aba tem período
      // próprio e o seletor global da página não vale aqui (achados 2 e 3).
      janela: janelaEfetiva(data),
    });
  }, [data, apelidoDoBot, onDadosChange]);

  // Com keepPreviousData o "Próxima" decide pelo `data` da página ANTERIOR —
  // duplo-clique rápido (ou total que encolheu entre uma página e outra) pede
  // um offset além do fim e a resposta volta itens=[] com total>0. Reclampa
  // para a última página válida em vez de deixar a tela fingindo vazio.
  useEffect(() => {
    if (!data) return;
    if (data.itens.length || data.total <= 0 || data.offset < data.total) return;
    const porPagina = Math.max(1, data.limit);
    setOffset(Math.floor((data.total - 1) / porPagina) * porPagina);
  }, [data]);

  // Foco da linha que o clique no popup apontava (`foco_ts` na URL): acende UMA
  // vez, na primeira resposta com algum item do recorte. O match é tolerante
  // (o foco_ts é aproximado — ver `utils/erroDoPopup`); recorte vazio nesta página
  // → nada acende, e adivinhar página seria pior do que destacar nada.
  useEffect(() => {
    if (focoAplicado.current || !daQuery.focoTs || !data?.itens.length) return;
    const achado = acharItemDoFoco(data.itens, daQuery);
    if (!achado) return;
    focoAplicado.current = true;
    setLinhaFoco(achado);
  }, [data, daQuery]);

  /** Ref da linha em foco: rola até ela assim que ela existe no DOM. */
  const refLinhaFoco = useCallback(
    (el: HTMLDivElement | null) => {
      // jsdom (e navegadores antigos) não implementam scrollIntoView — o destaque
      // visual não pode depender dele para acontecer.
      if (!el || !linhaFoco || typeof el.scrollIntoView !== 'function') return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    [linhaFoco],
  );

  // Value = nome REAL (é o que filtra); label = apelido. Bot filtrado que não
  // está no cadastro (chegou por chip do resumo) vira option órfã denunciada —
  // nunca some do select nem disfarça de opção válida (padrão do SelectRegime).
  const opcoesBot = useMemo(() => {
    const doCadastro = (vinculos ?? [])
      .map(v => ({ value: v.bot, label: apelidoDoBot(v.bot) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    if (bot && !doCadastro.some(op => op.value === bot)) {
      doCadastro.push({ value: bot, label: `${bot} (fora do cadastro)` });
    }
    return doCadastro;
  }, [vinculos, apelidoDoBot, bot]);

  const aviso = data ? avisoJanelaClampada(data) : null;
  const pag = data ? paginacaoAuditoria(data.total, data.limit, data.offset) : null;
  /**
   * TROCA de recorte em voo (página/filtro novo ainda carregando, mostrando o
   * data anterior) — distinta de "tem fetch acontecendo": com o polling ligado o
   * `isFetching` sobe a cada 5s SEM que nada tenha mudado na tela. Usar
   * `isFetching` cru para atenuar a tabela e travar "Próxima" faria o tick do
   * polling piscar a tela e desabilitar o botão sozinho a cada ciclo.
   */
  const trocandoRecorte = isFetching && isPlaceholderData;
  // "Próxima" espera a página em voo: o temProxima vem do data ANTERIOR
  // (keepPreviousData) — avançar de novo antes da resposta furaria o fim.
  const podeAvancar = Boolean(pag?.temProxima) && !trocandoRecorte;

  /**
   * Ocorrência em destaque (linha expandida ou linha em foco) que SUMIU da
   * página atual. Com o "Ao vivo" ligado a esteira anda sozinha: um erro novo
   * entra no topo e empurra o último item da página para a página seguinte —
   * o detalhe fecha e o flash de foco apaga sem ninguém ter clicado em nada.
   *
   * O aviso só diz que saiu. NÃO se promete para onde: descobrir em que página
   * o item foi parar exigiria outra consulta, e "navegar sozinho" seria pior do
   * que o sumiço (a tela pularia debaixo de quem está lendo).
   *
   * Só vale com o modo ligado e com resposta FRESCA na mão: durante uma troca
   * de recorte (`trocandoRecorte`) o que está na tela é a página anterior, e
   * filtrar/paginar por conta própria não é sumiço nenhum.
   */
  const emDestaque = expandida ?? linhaFoco;
  const saiuDaPagina = Boolean(
    aoVivo &&
      emDestaque &&
      data &&
      !trocandoRecorte &&
      !data.itens.some(it => it.id === emDestaque),
  );

  const temFiltroAtivo = Boolean(
    status !== 'erro' || bot || etapa || q || codiEmp || periodo.preset !== '31d',
  );

  const limparFiltros = () => {
    setPeriodo(rangeDoPresetAuditoria('31d'));
    setStatus('erro');
    setBot(null);
    setEtapa(null);
    setQ('');
    setQAplicado('');
    setCodiEmp('');
    setCodiEmpAplicado('');
    setOffset(0);
    setExpandida(null);
    // Limpar filtro larga também o destaque: a linha focada some do recorte.
    setLinhaFoco(null);
  };

  const aoClicarTipo = (tipo: string) => {
    const novo = qAplicado === tipo ? '' : tipo;
    setQ(novo);
    setQAplicado(novo);
    setOffset(0);
  };

  const aoClicarBot = (nome: string) => {
    setBot(atual => (atual === nome ? null : nome));
    setOffset(0);
  };

  // Rótulos alinhados com o filtro global da tela (21/09) para as duas réguas
  // falarem a mesma língua. A aba conta em HORAS (24h = as últimas 24 horas
  // corridas, não "desde 00h"), e o maior preset é 31 dias porque é o teto do
  // servidor — por isso "30 dias" carrega o `31d`, cobrindo a janela global
  // inteira sem estourar `_MAX_DIAS_JANELA`.
  const presets: Array<{ id: PresetAuditoria; label: string }> = [
    { id: '24h', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: '31d', label: '30 dias' },
  ];

  const inputPeriodoStyle: React.CSSProperties = {
    border: 'none',
    outline: 'none',
    fontFamily: FONTE_TEXTO,
    fontSize: '12px',
    color: 'oklch(0.3 0.02 165)',
    background: 'transparent',
  };

  const renderLinha = (item: IAuditoriaErroItem) => {
    const t = item.status === 'erro' ? TONES.err : TONES.warn;
    const etapaCel = celulaEtapa(item.etapa, item.etapa_raw);
    const mensagem = mensagemDaLinha(item.mensagem, item.mensagem_truncada);
    const aberta = expandida === item.id;
    const classificacao = classificacaoDaLinha(item);
    const emHover = hoverLinha === item.id;
    const emFoco = linhaFoco === item.id;
    const { detalhe } = item;
    const camposDetalhe = detalhe
      ? ([
          ['error_code', detalhe.error_code],
          ['error_nome', detalhe.error_nome],
          ['tipo_exception', detalhe.tipo_exception],
          ['modulo_id', detalhe.modulo_id === null ? null : String(detalhe.modulo_id)],
        ] as Array<[string, string | null]>)
      : [];
    return (
      <React.Fragment key={item.id}>
        <div
          ref={emFoco ? refLinhaFoco : undefined}
          className={emFoco ? 'orq-linha-foco' : undefined}
          data-testid={emFoco ? 'linha-em-foco' : undefined}
          onClick={() => setExpandida(atual => (atual === item.id ? null : item.id))}
          onMouseEnter={() => setHoverLinha(item.id)}
          onMouseLeave={() => setHoverLinha(null)}
          title={aberta ? 'Clique para recolher o detalhe' : 'Clique para expandir o detalhe'}
          style={{
            padding: '11px 22px',
            display: 'grid',
            gridTemplateColumns: gridColunas,
            gap: '14px',
            alignItems: 'center',
            border: `${emHover ? '2px' : '1px'} solid ${
              emHover ? 'oklch(0.45 0.1 165)' : 'oklch(0.96 0.006 160)'
            }`,
            marginBottom: '-1px',
            cursor: 'pointer',
            // Em foco o fundo é da animação (`.orq-linha-foco`): declarar inline
            // aqui venceria o keyframe e o flash simplesmente não apareceria.
            ...(emFoco ? {} : { background: aberta ? 'oklch(0.985 0.005 160)' : 'transparent' }),
          }}
        >
          <div style={{ fontFamily: FONTE_MONO, fontSize: '11px', color: 'oklch(0.45 0.02 165)' }}>
            {formatarTsAuditoria(item.ts)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
            {/*
              A célula é estreita e o apelido longo é truncado com reticências
              ("Transmissão MIT e DCT…"): o tooltip existe para COMPLETAR o que
              foi cortado, então ele repete o APELIDO por inteiro — mostrar o
              nome técnico aqui deixava o usuário sem saber o nome completo do
              que está lendo. O nome real não se perde: vai para a linha
              expandida, que é onde se investiga. `apelidoDoBot` já cai no nome
              real quando não há apelido cadastrado, então o title nunca vazio.
            */}
            <span
              title={apelidoDoBot(item.bot)}
              style={{
                fontSize: '12.5px',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {apelidoDoBot(item.bot)}
            </span>
            {item.tipo_bot && (
              <span style={{ fontFamily: FONTE_MONO, fontSize: '9.5px', color: C.faint }}>
                {item.tipo_bot}
              </span>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            {/*
              Ocorrência SEM empresa (achado 4 da auditoria 09/09): `codi_emp` e
              `nome_empresa` nulos não são dado faltando — é uma classe REAL de
              erro, o bot que quebrou ANTES de selecionar empresa (falha de login
              no Domínio, fail-safe do PyAutoGUI). Ela entra no total de
              ocorrências e some de toda agregação por empresa, e era justamente
              parte da diferença entre o "108" daqui e o "35 empresas travadas"
              do nó de Processos. O travessão neutro do NomeEmpresa escondia isso;
              aqui o caso é NOMEADO, em cinza (não é uma empresa, não pode ter o
              peso visual de uma) e com o title explicando a natureza da falha.
            */}
            {!item.codi_emp && !item.nome_empresa ? (
              <span
                title="Erro de infraestrutura do bot: quebrou antes de selecionar a empresa (ex.: falha de login no Domínio, fail-safe do PyAutoGUI). Conta no total de ocorrências, mas não aparece em nenhuma contagem por empresa."
                style={{ fontSize: '12.5px', fontStyle: 'italic', color: C.faint }}
              >
                sem empresa
              </span>
            ) : (
              <NomeEmpresa
                variante="bloco"
                nome={item.nome_empresa}
                codigo={item.codi_emp}
                style={{ fontSize: '12.5px', fontWeight: 600 }}
              />
            )}
          </div>
          <div style={{ fontFamily: FONTE_MONO, fontSize: '11px', color: 'oklch(0.5 0.015 165)' }}>
            {item.competencia ?? '—'}
          </div>
          <div
            title={etapaCel.title}
            style={{
              fontSize: '12px',
              color: 'oklch(0.45 0.015 165)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {etapaCel.texto}
          </div>
          <div style={{ minWidth: 0 }}>
            {/*
              O fecho de unidade traz o CÓDIGO do catálogo ("SEM_PROCURACAO"),
              que classifica melhor que o `tipo_erro` derivado da frase — este
              último nasce de regex sobre a mensagem e, num motivo sem classe de
              exceção, vira um prefixo truncado. Quando o código existe ele
              vence; o `title` mostra o número para quem for cruzar com o
              catálogo.
            */}
            {classificacao ? (
              <span
                title={classificacao.title}
                style={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  padding: '2px 7px',
                  borderRadius: '5px',
                  background: C.track,
                  fontFamily: FONTE_MONO,
                  fontSize: '10.5px',
                  color: 'oklch(0.4 0.02 165)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {classificacao.texto}
              </span>
            ) : (
              <span style={{ color: C.faint, fontSize: '12px' }}>—</span>
            )}
          </div>
          <div
            title={mensagem}
            style={{
              fontSize: '12px',
              color: 'oklch(0.42 0.02 165)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {mensagem}
          </div>
          <div>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '2px 8px',
                borderRadius: '999px',
                background: t.bg,
                color: t.fg,
                fontFamily: FONTE_MONO,
                fontSize: '10px',
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  background: t.dot,
                  flex: 'none',
                }}
              />
              {item.status}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            {/*
              Linha SEM print vinda do fecho de unidade: diz que a fonte não
              tem print, em vez de deixar o espaço vazio. A ausência aqui é
              característica da fonte (o fecho só ganha print quando há
              evidência casando exec_id+empresa), não defeito — e sem a marca
              o usuário leria como bug, que foi o pedido explícito.
            */}
            {item.origem === 'unidade' && !item.tem_print && (
              <span
                title={
                  'Resumo de operação: a empresa não foi processada. ' +
                  'Esta origem só tem print quando o bot registrou evidência.'
                }
                style={{
                  padding: '3px 8px',
                  borderRadius: '7px',
                  border: '1px dashed oklch(0.86 0.01 160)',
                  fontSize: '10.5px',
                  color: C.faint,
                  whiteSpace: 'nowrap',
                  flex: 'none',
                }}
              >
                sem print
              </span>
            )}
            {item.tem_print && item.print_key && (
              <div
                onClick={ev => {
                  ev.stopPropagation();
                  setPrintKey(item.print_key);
                }}
                title="Ver o print desta ocorrência"
                style={{
                  padding: '4px 10px',
                  borderRadius: '7px',
                  border: '1px solid oklch(0.9 0.006 160)',
                  background: 'oklch(1 0 0)',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'oklch(0.4 0.08 165)',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                Ver print
              </div>
            )}
            <span style={{ fontSize: '10px', color: C.faint, flex: 'none' }}>
              {aberta ? '▾' : '▸'}
            </span>
          </div>
        </div>
        {aberta && (
          <div
            style={{
              padding: '12px 22px 16px',
              background: 'oklch(0.98 0.006 160)',
              border: '1px solid oklch(0.96 0.006 160)',
              borderTop: 'none',
              marginBottom: '-1px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px 28px',
            }}
          >
            <CampoDetalhe label="Mensagem" largo>
              <span style={{ whiteSpace: 'pre-wrap' }}>{mensagem}</span>
            </CampoDetalhe>
            {camposDetalhe
              .filter(([, valor]) => valor !== null && valor !== '')
              .map(([label, valor]) => (
                <CampoDetalhe key={label} label={label}>
                  <span style={{ fontFamily: FONTE_MONO, fontSize: '11.5px' }}>{valor}</span>
                </CampoDetalhe>
              ))}
            {detalhe?.motivo && <CampoDetalhe label="Motivo">{detalhe.motivo}</CampoDetalhe>}
            {item.etapa_raw && item.etapa_raw !== etapaCel.texto && (
              <CampoDetalhe label="Etapa (como o bot enviou)">
                <span style={{ fontFamily: FONTE_MONO, fontSize: '11.5px' }}>{item.etapa_raw}</span>
              </CampoDetalhe>
            )}
            {/*
              Nome TÉCNICO do bot, ao lado de "Execução": a célula da tabela
              mostra o apelido (leitura rápida do dia a dia), mas quem abre o
              detalhe está investigando — e é o nome real que casa com o log e
              com o filtro. Sempre presente (`item.bot` é obrigatório no
              contrato), então não leva guarda como os campos opcionais.
              Rótulo "Nome do bot" e não "Bot": ao lado do apelido que está logo
              acima na tabela, "Bot" sozinho seria ambíguo sobre qual dos dois é.
            */}
            <CampoDetalhe label="Nome do bot">
              <span style={{ fontFamily: FONTE_MONO, fontSize: '11.5px' }}>{item.bot}</span>
            </CampoDetalhe>
            {item.exec_id && (
              <CampoDetalhe label="Execução">
                <span style={{ fontFamily: FONTE_MONO, fontSize: '11.5px' }}>{item.exec_id}</span>
              </CampoDetalhe>
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  // Mini-cards do topo: agregado do MESMO filtro da tabela (top 10 do servidor).
  // O clique no chip de TIPO aplica a busca `q` (não há filtro tipo_erro no
  // endpoint; o tipo deriva da mensagem, então a busca filtra a mesma
  // população que o agrupou); o de BOT aplica o filtro exato de bot.
  //
  // O número do chip é OCORRÊNCIA, nunca empresa (achado 5 da auditoria 09/09):
  // "Apuração Lucro Presumido · 108" lido ao lado do "35 empresas travadas" do nó
  // de Processos parecia contradição, e não é — a mesma empresa reaparece a cada
  // retentativa do bot. Cada title abre com a unidade e a ressalva da repetição
  // (`unidadeDoChip`).
  interface ChipResumoDef {
    chave: string;
    label: string;
    qtd: number;
    mono: boolean;
    ativo: boolean;
    title: string;
    onClick?: () => void;
  }
  const cardsResumo: Array<{ titulo: string; chips: ChipResumoDef[] }> = [
    {
      titulo: 'Top tipos de erro',
      chips: (data?.resumo.por_tipo ?? []).map(r => ({
        chave: r.tipo_erro ?? '(sem classificação)',
        label: r.tipo_erro ?? '(sem classificação)',
        qtd: r.qtd,
        mono: true,
        ativo: Boolean(r.tipo_erro) && qAplicado === r.tipo_erro,
        title: r.tipo_erro
          ? `${unidadeDoChip(r.qtd)}. Clique para filtrar por este tipo (aplica a busca textual — é da mensagem que o tipo deriva).`
          : `${unidadeDoChip(r.qtd)} sem classe de exceção nem prefixo estável — sem filtro direto.`,
        onClick: r.tipo_erro ? () => aoClicarTipo(r.tipo_erro as string) : undefined,
      })),
    },
    {
      titulo: 'Top bots',
      chips: (data?.resumo.por_bot ?? []).map(r => ({
        chave: r.bot,
        label: apelidoDoBot(r.bot),
        qtd: r.qtd,
        mono: false,
        ativo: bot === r.bot,
        title: `${temApelido(r.bot) ? `${r.bot} · ` : ''}${unidadeDoChip(r.qtd)}. Clique para filtrar por este bot.`,
        onClick: () => aoClicarBot(r.bot),
      })),
    },
  ];

  // Corpo da tabela decidido FORA do JSX (sem ternário aninhado): carregando →
  // além do fim → vazio honesto → linhas.
  let corpoTabela: React.ReactNode;
  if (!data && isLoading) {
    corpoTabela = (
      <div
        style={{
          padding: '34px 22px',
          textAlign: 'center',
          fontSize: '12.5px',
          fontStyle: 'italic',
          color: C.sub,
        }}
      >
        Carregando ocorrências…
      </div>
    );
  } else if (data && !data.itens.length && data.total > 0 && data.offset > 0) {
    // Offset além do fim (o efeito de reclampe já está voltando a página):
    // com total>0 o texto de vazio seria FALSO — nunca mostrá-lo aqui.
    corpoTabela = (
      <div
        style={{
          padding: '34px 22px',
          textAlign: 'center',
          fontSize: '12.5px',
          fontStyle: 'italic',
          color: C.sub,
        }}
      >
        Página além do fim — voltando para a última página…
      </div>
    );
  } else if (data && !data.itens.length) {
    corpoTabela = (
      <div
        style={{
          padding: '34px 22px',
          textAlign: 'center',
          fontSize: '12.5px',
          color: C.sub,
        }}
      >
        Nenhuma ocorrência para os filtros atuais.
        {status === 'erro' && (
          <div style={{ marginTop: '4px', fontSize: '11.5px', color: C.faint }}>
            Warnings ficam de fora por padrão — use a pílula Warning ou Ambos para incluí-los.
          </div>
        )}
      </div>
    );
  } else {
    corpoTabela = (data?.itens ?? []).map(renderLinha);
  }

  return (
    <div style={fundoDaAba('erros')}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
          gap: '14px',
        }}
      >
        {cardsResumo.map((card, ci) => (
          <div
            key={card.titulo}
            style={{
              ...CARTAO,
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              minWidth: 0,
            }}
          >
            <TituloSecao
              aba="erros"
              icone={<Icone nome={ci === 0 ? 'alerta' : 'raio'} tamanho={16} />}
              titulo={card.titulo}
              sub={
                ci === 0
                  ? 'O que mais quebrou no recorte. Clique para filtrar pela mensagem.'
                  : 'Quem mais registrou ocorrência. Clique para filtrar pelo bot.'
              }
            />
            {card.chips.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {card.chips.map(chip => (
                  <ChipResumo
                    key={chip.chave}
                    label={chip.label}
                    qtd={chip.qtd}
                    ativo={chip.ativo}
                    mono={chip.mono}
                    title={chip.title}
                    onClick={chip.onClick}
                  />
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '11.5px', fontStyle: 'italic', color: C.faint }}>
                sem ocorrências no recorte atual
              </div>
            )}
          </div>
        ))}
      </div>

      <div
        style={{
          ...CARTAO,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            minWidth: 0,
          }}
        >
          <TituloSecao
            aba="erros"
            icone={<Icone nome="erros" tamanho={17} />}
            titulo="Ocorrências"
            sub="Cada linha é um registro de erro ou warning gravado pelos bots, mais recente primeiro."
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/*
              Toggle "Ao vivo": não é um filtro (não muda o recorte), é o modo de
              atualização da tela — por isso abre a linha de controles, separado
              das pílulas/selects, e o "Limpar filtros" NÃO o desliga.
              Elemento <button> de verdade com role="switch"/aria-checked: o
              trilho é operável por teclado e anunciado como interruptor, ao
              contrário das pílulas de status (divs herdadas do protótipo).
            */}
            <button
              type="button"
              role="switch"
              aria-checked={aoVivo}
              onClick={() => setAoVivo(atual => !atual)}
              title={
                aoVivo
                  ? `Ao vivo LIGADO — a tabela se atualiza sozinha a cada ${INTERVALO_AO_VIVO_MS / 1000}s (pausa quando a aba fica em segundo plano). Clique para desligar.`
                  : `Atualizar a tabela sozinha a cada ${INTERVALO_AO_VIVO_MS / 1000}s. Fica desligado por padrão: a auditoria varre bastante dado, então só consulta em loop quando você pede.`
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '6px 11px 6px 9px',
                borderRadius: '20px',
                border: `1px solid ${aoVivo ? 'oklch(0.62 0.15 162)' : 'oklch(0.9 0.006 160)'}`,
                background: aoVivo ? 'oklch(0.94 0.04 162)' : 'oklch(1 0 0)',
                color: aoVivo ? 'oklch(0.4 0.1 165)' : C.sub,
                fontFamily: FONTE_TEXTO,
                fontSize: '11.5px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              <span
                aria-hidden="true"
                className={aoVivo ? 'orq-pulso-ao-vivo' : undefined}
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: aoVivo ? 'oklch(0.62 0.15 162)' : 'oklch(0.82 0.008 160)',
                  flex: 'none',
                }}
              />
              Ao vivo
              {/* Estado em PALAVRA, não só em cor: o toggle continua legível para
                  quem não distingue o verde do cinza. `aria-hidden` porque o
                  estado JÁ vai no `aria-checked` do switch — sem isso o leitor de
                  tela anuncia duas vezes ("Ao vivo, ativado… off") e o nome
                  acessível do botão vira o colado "Ao vivooff" (o JSX não põe
                  espaço entre os nós; o respiro aqui é só o `gap` do flex). */}
              <span
                aria-hidden="true"
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '9.5px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  color: aoVivo ? 'oklch(0.45 0.09 165)' : C.faint,
                }}
              >
                {aoVivo ? 'on' : 'off'}
              </span>
            </button>
            <div
              style={{
                display: 'flex',
                gap: '3px',
                padding: '3px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.55)',
                border: '1px solid rgba(255,255,255,0.9)',
                boxShadow: 'inset 0 1px 2px rgba(16,60,48,0.07)',
                fontSize: '12px',
              }}
            >
              {presets.map(p => {
                const ativo = periodo.preset === p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      setPeriodo(rangeDoPresetAuditoria(p.id));
                      setOffset(0);
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '9px',
                      cursor: 'pointer',
                      background: ativo ? C.dark : 'transparent',
                      color: ativo ? C.onDark : C.sub,
                      fontWeight: ativo ? 600 : 500,
                      boxShadow: ativo ? '0 4px 12px rgba(16,40,34,0.25)' : 'none',
                    }}
                  >
                    {p.label}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(255,255,255,0.9)',
                color: C.sub,
              }}
            >
              <Icone nome="calendario" tamanho={14} />
              <input
                type="date"
                value={periodo.de}
                onChange={ev => {
                  setPeriodo(atual => ({ preset: null, de: ev.target.value, ate: atual.ate }));
                  setOffset(0);
                }}
                style={inputPeriodoStyle}
              />
              <span style={{ color: 'oklch(0.7 0.01 165)', fontSize: '12px' }}>→</span>
              <input
                type="date"
                value={periodo.ate}
                onChange={ev => {
                  setPeriodo(atual => ({ preset: null, de: atual.de, ate: ev.target.value }));
                  setOffset(0);
                }}
                style={inputPeriodoStyle}
              />
            </div>
            {/* Pílulas de status: erro/warning nos tons da paleta; "Ambos" neutro. */}
            {OPCOES_STATUS.map(op => {
              const ativo = status === op.valor;
              let tone = op.valor === 'erro' ? TONES.err : TONES.warn;
              const neutro = op.valor === 'ambos';
              if (neutro) tone = TONES.idle;
              const borda = neutro ? 'oklch(0.85 0.008 160)' : tone.dot;
              const fundoAtivo = neutro ? C.dark : tone.dot;
              let corTexto = neutro ? C.sub : tone.fg;
              if (ativo) corTexto = 'oklch(1 0 0)';
              return (
                <div
                  key={op.valor}
                  onClick={() => {
                    setStatus(op.valor);
                    setOffset(0);
                  }}
                  title={`Mostrar ${op.valor === 'ambos' ? 'erros e warnings' : `só ${op.label.toLowerCase()}s`}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '5px 11px',
                    borderRadius: '20px',
                    border: `1px solid ${borda}`,
                    background: ativo ? fundoAtivo : 'rgba(255,255,255,0.7)',
                    boxShadow: ativo
                      ? `0 4px 12px color-mix(in oklch, ${fundoAtivo} 35%, transparent)`
                      : 'none',
                    color: corTexto,
                    fontSize: '11.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                >
                  {op.label}
                </div>
              );
            })}
            <SelectFiltro
              value={bot ?? ''}
              onChange={novo => {
                setBot(novo || null);
                setOffset(0);
              }}
              opcoes={[{ value: '', label: 'Todos os bots' }, ...opcoesBot]}
              selectStyle={{ maxWidth: '200px' }}
            />
            <SelectFiltro
              value={etapa ?? ''}
              onChange={novo => {
                setEtapa(novo || null);
                setOffset(0);
              }}
              opcoes={[
                { value: '', label: 'Todas as etapas' },
                ...ETAPAS_AUDITORIA.map(e => ({ value: e, label: e })),
              ]}
              selectStyle={{ maxWidth: '180px' }}
            />
            {/*
              EMPRESA por ID ou por RAZÃO SOCIAL (pedido 19/10: "igual está na
              Resumo", com ID como padrão). Os dois modos escrevem no MESMO filtro
              (`codi_emp` da auditoria): o ID passa pelo debounce de sempre; a
              escolha na lista de nomes aplica na hora — é um clique, não digitação.
              Trocar de modo limpa a empresa, como no Resumo (o termo de um modo
              não serve no outro).
            */}
            <div
              style={{
                display: 'flex',
                alignItems: 'stretch',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.95)',
                background: 'rgba(255,255,255,0.78)',
              }}
            >
              <SelectFiltro
                value={modoEmpresa}
                onChange={novo => {
                  setModoEmpresa(novo === 'razao' ? 'razao' : 'id');
                  setCodiEmp('');
                  setCodiEmpAplicado('');
                }}
                opcoes={[
                  { value: 'id', label: 'ID' },
                  { value: 'razao', label: 'Razão social' },
                ]}
                wrapperStyle={{ borderRight: '1px solid oklch(0.9 0.006 160)' }}
                selectStyle={{
                  border: 'none',
                  borderRadius: '12px 0 0 12px',
                  background: 'transparent',
                  fontWeight: 600,
                }}
              />
              {modoEmpresa === 'id' ? (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 10px' }}
                >
                  <Icone nome="empresa" tamanho={14} style={{ color: C.faint }} />
                  <input
                    type="text"
                    placeholder="Cód. empresa"
                    value={codiEmp}
                    onChange={ev => setCodiEmp(ev.target.value)}
                    style={{
                      width: '96px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: FONTE_MONO,
                      fontSize: '12px',
                      color: 'oklch(0.3 0.02 165)',
                      outline: 'none',
                    }}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                  <BuscaEmpresa
                    opcoes={opcoesBuscaEmpresa}
                    valor={codiEmpAplicado || null}
                    onEscolher={codi => {
                      setCodiEmp(codi ?? '');
                      setCodiEmpAplicado(codi ?? '');
                    }}
                  />
                </div>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.95)',
                background: 'rgba(255,255,255,0.78)',
              }}
            >
              <span style={{ fontSize: '12.5px', color: 'oklch(0.62 0.012 165)' }}>⌕</span>
              <input
                type="text"
                placeholder="Buscar na mensagem"
                value={q}
                onChange={ev => setQ(ev.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  fontFamily: FONTE_TEXTO,
                  fontSize: '12.5px',
                  color: 'oklch(0.3 0.02 165)',
                  width: '150px',
                  background: 'transparent',
                }}
              />
            </div>
            {temFiltroAtivo && (
              <div
                onClick={limparFiltros}
                title="Limpar filtros"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '10px',
                  border: `1px solid color-mix(in oklch, ${C.red} 30%, transparent)`,
                  background: 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  color: C.redFg,
                  fontSize: '13px',
                }}
              >
                ✕
              </div>
            )}
          </div>
        </div>

        {aviso && (
          <div
            style={{
              padding: '0 22px 12px',
              fontSize: '11.5px',
              color: C.amberFg,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>⚠</span>
            {aviso}
          </div>
        )}

        {/* Aviso do sumiço (ver `saiuDaPagina`): discreto, na mesma gramática do
            aviso do clamp, e sem prometer para onde a ocorrência foi. */}
        {saiuDaPagina && (
          <div
            data-testid="aviso-saiu-da-pagina"
            role="status"
            style={{
              padding: '0 22px 12px',
              fontSize: '11.5px',
              color: C.sub,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span aria-hidden="true">↷</span>A ocorrência em destaque saiu da página atual — a lista
            se atualizou sozinha.
          </div>
        )}

        {isError && (
          <div
            style={{
              margin: '0 22px 14px',
              border: '1px solid oklch(0.85 0.08 28)',
              borderRadius: '8px',
              background: 'oklch(0.97 0.02 28)',
              padding: '10px 14px',
              fontSize: '12.5px',
              color: 'oklch(0.4 0.1 28)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>⚠</span>
            Não deu para carregar as ocorrências agora — tente ajustar o período ou recarregar a
            página.
          </div>
        )}

        <div
          style={{
            padding: '10px 22px',
            display: 'grid',
            gridTemplateColumns: gridColunas,
            background: 'rgba(255,255,255,0.45)',
            borderTop: '1px solid rgba(16,60,48,0.06)',
            gap: '14px',
            fontFamily: FONTE_MONO,
            fontSize: '10px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'oklch(0.6 0.012 165)',
            borderBottom: '1px solid oklch(0.9 0.008 160)',
          }}
        >
          {COLUNAS.map(label => (
            <div key={label || 'acoes'}>{label}</div>
          ))}
        </div>

        {/* Atenua só na TROCA de recorte — o tick do polling repinta os dados em
            silêncio (o sinal de atividade dele é a bolinha do toggle). O que o
            tick pode levar junto é a linha em destaque, se ela sair da página:
            isso é anunciado pelo aviso de `saiuDaPagina`, não pela opacidade. */}
        <div
          data-testid="corpo-tabela"
          style={{ opacity: trocandoRecorte && data ? 0.6 : 1, transition: 'opacity 0.15s' }}
        >
          {corpoTabela}
        </div>

        {data && pag && (
          <div
            style={{
              padding: '14px 22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            {/*
              O rodapé é o único lugar da aba que fecha a conta de unidade: total
              em OCORRÊNCIAS, mais quantas EMPRESAS distintas isso representa e
              quantas ocorrências não têm empresa nenhuma (achados 4 e 5). É a
              tradução que faltava entre "108 ocorrências" aqui e "35 empresas
              travadas" no nó de Processos. O escopo (filtro inteiro × só esta
              página) é decidido e ROTULADO em `sufixoRodapeErros` NÚMERO A NÚMERO,
              conforme o backend já tenha ou não CADA campo — num deploy parcial um
              deles pode vir do filtro e o outro da página, e a frase diz qual é qual.
            */}
            <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>
              {`Mostrando ${pag.inicio}–${pag.fim} de ${data.total} ocorrência${
                data.total === 1 ? '' : 's'
              }${sufixoRodapeErros(data).texto} no filtro atual (página ${pag.paginaAtual} de ${pag.totalPaginas}).`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                onClick={() => pag.temAnterior && setOffset(Math.max(0, offset - LIMITE_PAGINA))}
                style={{
                  ...estiloPilula(false, 'erros'),
                  fontSize: '12.5px',
                  color: pag.temAnterior ? 'oklch(0.3 0.02 165)' : 'oklch(0.78 0.01 165)',
                  cursor: pag.temAnterior ? 'pointer' : 'default',
                }}
              >
                ← Anterior
              </div>
              <div
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '11.5px',
                  color: 'oklch(0.5 0.015 165)',
                  whiteSpace: 'nowrap',
                }}
              >
                Página {pag.paginaAtual} de {pag.totalPaginas}
              </div>
              <div
                data-testid="pag-proxima"
                onClick={() => podeAvancar && setOffset(offset + LIMITE_PAGINA)}
                style={{
                  ...estiloPilula(false, 'erros'),
                  fontSize: '12.5px',
                  color: podeAvancar ? 'oklch(0.3 0.02 165)' : 'oklch(0.78 0.01 165)',
                  cursor: podeAvancar ? 'pointer' : 'default',
                }}
              >
                Próxima →
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalPrint printKey={printKey} onClose={() => setPrintKey(null)} />
    </div>
  );
};
